/* ═══════════════════════════════════════════════════════════════════════════
   v3.3 — RISK REGISTER controllers. New Risks tab. Mirrors Stakeholders
   pattern: cards on top, P×I matrix heatmap below. Each risk has a TYPE
   (threat | opportunity); the matrix toggles between threat severity coloring
   and opportunity positive coloring.
   ═══════════════════════════════════════════════════════════════════════════ */

/* v13.1 (A37): _RISK_RANK / _RISK_CAP / _riskScore / _riskBand used to be defined a
   SECOND time here, with 'med' where the other copy said 'medium'. They now live once, in
   the risk model block beside _normRisk. Do not re-add a local copy. */
/* Score badge styling: threats use severity (red/amber/green); opportunities
   use a positive label ("High potential" / "Moderate" / "Low potential") and
   the teal palette. Returns {cls, text}. */
function _riskScoreBadge(r) {
  const s = _riskScore(r);
  if (r.type === 'opportunity') {
    return { cls: 'opp-pos', text: `Score ${s} · ${_OPP_WORD[_riskBand(s)]}` };
  }
  const b = _riskBand(s);
  return { cls: _SEV_CLASS[b], text: `Score ${s} · ${_RISK_CAP(b)}` };
}

/* ── Card list rendering ─────────────────────────────────────────────────── */
function renderRisks() {
  const host = document.getElementById('riskList');
  const empty = document.getElementById('riskEmpty');
  const emptyFiltered = document.getElementById('riskEmptyFiltered');
  if (!host) return;
  const qEl = document.getElementById('riskSearchInput');
  const ftEl = document.getElementById('riskFilterType');
  const sortEl = document.getElementById('riskSortBy');
  const q = (qEl ? qEl.value : '').toLowerCase().trim();
  const ft = ftEl ? ftEl.value : 'all';
  const sort = sortEl ? sortEl.value : 'score-desc';
  let rows = RISKS.filter(r => {
    /* v8.4: the type dropdown now also filters by phase — a promoted threat reads as an
       Issue, so 'threat' shows only risk-phase threats and 'issue' shows the issue phase. */
    if (ft === 'threat' && !(r.type === 'threat' && r.phase !== 'issue')) return false;
    if (ft === 'opportunity' && r.type !== 'opportunity') return false;
    if (ft === 'issue' && r.phase !== 'issue') return false;
    if (q) {
      const hay = `${r.statement} ${r.category} ${r.strategy} ${r.owner} ${r.notes} ${r.resolution || ''} ${r.severity || ''} ${r.workStatus || ''} ${_registerId(r)}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  rows.sort((a, b) => {
    if (sort === 'score-desc') return _riskScore(b) - _riskScore(a);
    if (sort === 'score-asc') return _riskScore(a) - _riskScore(b);
    if (sort === 'category') return a.category.localeCompare(b.category);
    if (sort === 'type') return a.type.localeCompare(b.type);
    return 0;
  });
  host.innerHTML = '';
  if (!RISKS.length) {
    if (empty) empty.style.display = '';
    if (emptyFiltered) emptyFiltered.style.display = 'none';
  } else if (!rows.length) {
    if (empty) empty.style.display = 'none';
    if (emptyFiltered) emptyFiltered.style.display = '';
  } else {
    if (empty) empty.style.display = 'none';
    if (emptyFiltered) emptyFiltered.style.display = 'none';
  }
  rows.forEach(r => host.appendChild(_buildRiskCard(r)));
  /* register count: "N risks · M issues" */
  const cEl = document.getElementById('raidRegisterCount');
  if (cEl) {
    const nIss = RISKS.filter(r => r.phase === 'issue').length;
    const nRisk = RISKS.length - nIss;
    cEl.textContent = RISKS.length ? `${nRisk} risk${nRisk === 1 ? '' : 's'} · ${nIss} issue${nIss === 1 ? '' : 's'}` : '';
  }
  renderRiskMatrix();
}

function _buildRiskCard(r) {
  const isIssue = r.phase === 'issue';
  const card = document.createElement('div');
  card.className = 'risk-card type-' + r.type + (isIssue ? ' is-issue' : '');
  card.dataset.id = r.id;
  const statusCls = r.status === 'Occurred' ? 'st-occurred' : r.status === 'Closed' ? 'st-closed' : '';

  /* ── Top row: pill · register id · category · [score, risk phase only] · status · delete ── */
  const top = document.createElement('div');
  top.className = 'rc-top';
  const scoreHtml = isIssue ? '' : (function () { const sb = _riskScoreBadge(r); return `<span class="rc-score ${sb.cls}">${esc(sb.text)}</span>`; })();
  top.innerHTML =
    `<span class="rc-type${isIssue ? ' rc-type-fixed' : ''}"${isIssue ? '' : ' title="Click to flip type"'}>${_riskPillLabel(r)}</span>` +
    `<span class="rc-regid" title="Register reference — derived from position, so deleting an earlier risk renumbers the ones after it">${esc(_registerId(r))}</span>` +   /* v15.2: "stable for life" described the pre-v14 stored-id scheme; since W3b the ref is a derived ordinal and a delete DOES renumber later rows — the tooltip was a false promise about numbers people quote in emails */
    `<select class="rc-cat" title="Category">` +
      /* v13.1 (A39, ◆1): value = the STORED token, label = the British spelling */
      RISK_VALID_CATEGORIES.map(c => `<option value="${esc(c)}"${c === r.category ? ' selected' : ''}>${esc(riskCategoryLabel(c))}</option>`).join('') +
    `</select>` +
    '<span class="rc-spacer"></span>' +
    scoreHtml +
    `<select class="rc-status ${statusCls}" title="Risk status — set a threat to Occurred to promote it to an issue; back to Open to demote">` +
      RISK_VALID_STATUSES.map(s => `<option value="${esc(s)}"${s === r.status ? ' selected' : ''}>${esc(s)}</option>`).join('') +
    `</select>` +
    `<span class="rc-card-actions"><button class="rc-delete" title="Delete">×</button></span>`;
  if (!isIssue) {
    top.querySelector('.rc-type').addEventListener('click', () => {
      r.type = (r.type === 'threat') ? 'opportunity' : 'threat';
      const vs = (r.type === 'threat') ? RISK_THREAT_STRATEGIES : RISK_OPPORTUNITY_STRATEGIES;
      if (!vs.includes(r.strategy)) r.strategy = vs[vs.length - 1];
      /* ── v13.1 (A17): the phase depends on the TYPE, so re-derive it ─────────────────
         Promote-in-place is threat-only: an occurred THREAT becomes an issue, an occurred
         opportunity stays a risk. This handler changed the type and fixed the strategy but
         never revisited the phase — so flipping an OCCURRED opportunity to a threat left an
         occurred threat still filed as a risk, a state the register cannot produce any
         other way and the promote rule says should not exist. Re-running the status setter
         with the unchanged status is exactly the derivation; it is idempotent. */
      _setRiskStatus(r, r.status);
      markDirty(); _afterRiskMutation();
    });
  }
  top.querySelector('.rc-cat').addEventListener('change', (e) => {
    /* v13.1 (A50): _afterRiskMutation, like every sibling handler on this card. This one
       refreshed the dashboard but never re-rendered the register, so with the list sorted
       BY CATEGORY the card you had just re-categorised stayed exactly where it was — the
       one sort order in which the change is guaranteed to be visible. */
    if (RISK_VALID_CATEGORIES.includes(e.target.value)) { r.category = e.target.value; markDirty(); _afterRiskMutation(); }
  });
  top.querySelector('.rc-status').addEventListener('change', (e) => {
    if (RISK_VALID_STATUSES.includes(e.target.value)) { _setRiskStatus(r, e.target.value); markDirty(); _afterRiskMutation(); }
  });
  top.querySelector('.rc-delete').addEventListener('click', () => deleteRisk(r.id));
  card.appendChild(top);

  /* ── Statement (both phases) ── */
  const stmt = document.createElement('div');
  stmt.className = 'rc-statement';
  stmt.contentEditable = 'true';
  stmt.spellcheck = false;
  stmt.textContent = r.statement || '';
  _onBlurCommit(stmt, () => { const v = (stmt.textContent || '').trim(); if (v !== (r.statement || '')) { r.statement = v; markDirty(); } });
  card.appendChild(stmt);

  if (!isIssue) {
    /* ── Risk-phase meta: P / I / Strategy / Owner ── */
    const meta = document.createElement('div');
    meta.className = 'rc-meta';
    const probField = document.createElement('span'); probField.className = 'rc-meta-field';
    probField.innerHTML = `P: <select>${RISK_VALID_LEVELS.map(l => `<option value="${l}"${l === r.probability ? ' selected' : ''}>${_RISK_CAP(l)}</option>`).join('')}</select>`;
    probField.querySelector('select').addEventListener('change', (e) => { if (RISK_VALID_LEVELS.includes(e.target.value)) { r.probability = e.target.value; markDirty(); _afterRiskMutation(); } });
    meta.appendChild(probField);
    const impField = document.createElement('span'); impField.className = 'rc-meta-field';
    impField.innerHTML = `I: <select>${RISK_VALID_LEVELS.map(l => `<option value="${l}"${l === r.impact ? ' selected' : ''}>${_RISK_CAP(l)}</option>`).join('')}</select>`;
    impField.querySelector('select').addEventListener('change', (e) => { if (RISK_VALID_LEVELS.includes(e.target.value)) { r.impact = e.target.value; markDirty(); _afterRiskMutation(); } });
    meta.appendChild(impField);
    const stratField = document.createElement('span'); stratField.className = 'rc-meta-field';
    const vs = (r.type === 'threat') ? RISK_THREAT_STRATEGIES : RISK_OPPORTUNITY_STRATEGIES;
    stratField.innerHTML = `Strategy: <select>${vs.map(s => `<option value="${esc(s)}"${s === r.strategy ? ' selected' : ''}>${esc(s)}</option>`).join('')}</select>`;
    stratField.querySelector('select').addEventListener('change', (e) => { if (vs.includes(e.target.value)) { r.strategy = e.target.value; markDirty(); _refreshDashboardFromRisks(); } });
    meta.appendChild(stratField);
    meta.appendChild(_rcOwnerField(r));
    card.appendChild(meta);
  } else {
    /* ── Issue-phase body: History (promoted only) · Raised/Severity/Work status/Owner · Resolution ── */
    if (!r.direct) {
      const hist = document.createElement('div');
      hist.className = 'rc-history';
      hist.innerHTML = `<span class="rc-history-tag">History</span> Assessed as <strong>${_RISK_CAP(r.probability)}</strong> probability × <strong>${_RISK_CAP(r.impact)}</strong> impact · planned response <strong>${esc(r.strategy)}</strong> before it occurred.`;
      card.appendChild(hist);
    }
    const meta = document.createElement('div');
    meta.className = 'rc-meta rc-meta-issue';
    const raisedField = document.createElement('span'); raisedField.className = 'rc-meta-field';
    raisedField.innerHTML = `Raised: <input type="date" value="${esc(r.raised || '')}">`;
    raisedField.querySelector('input').addEventListener('change', (e) => { r.raised = e.target.value || ''; markDirty(); });
    meta.appendChild(raisedField);
    const sevField = document.createElement('span'); sevField.className = 'rc-meta-field';
    sevField.innerHTML = `Severity: <select>${ISSUE_SEVERITIES.map(s => `<option value="${esc(s)}"${s === r.severity ? ' selected' : ''}>${esc(s)}</option>`).join('')}</select>`;
    sevField.querySelector('select').addEventListener('change', (e) => { if (ISSUE_SEVERITIES.includes(e.target.value)) { r.severity = e.target.value; markDirty(); _afterRiskMutation(); } });
    meta.appendChild(sevField);
    const wsField = document.createElement('span'); wsField.className = 'rc-meta-field';
    wsField.innerHTML = `Work status: <select>${ISSUE_WORKSTATUSES.map(s => `<option value="${esc(s)}"${s === r.workStatus ? ' selected' : ''}>${esc(s)}</option>`).join('')}</select>`;
    wsField.querySelector('select').addEventListener('change', (e) => { if (ISSUE_WORKSTATUSES.includes(e.target.value)) { r.workStatus = e.target.value; markDirty(); } });
    meta.appendChild(wsField);
    meta.appendChild(_rcOwnerField(r));
    card.appendChild(meta);
    const resWrap = document.createElement('div'); resWrap.className = 'rc-resolution-wrap';
    const resLabel = document.createElement('div'); resLabel.className = 'rc-sublabel'; resLabel.textContent = 'Resolution';
    const res = document.createElement('div'); res.className = 'rc-resolution'; res.contentEditable = 'true'; res.spellcheck = false; res.textContent = r.resolution || '';
    res.setAttribute('data-placeholder', 'How is/was this issue resolved?');
    _onBlurCommit(res, () => { const v = (res.textContent || '').trim(); if (v !== (r.resolution || '')) { r.resolution = v; markDirty(); } });
    resWrap.appendChild(resLabel); resWrap.appendChild(res);
    card.appendChild(resWrap);
  }

  /* ── Notes (both phases) ── */
  const notes = document.createElement('div');
  notes.className = 'rc-notes';
  notes.contentEditable = 'true';
  notes.spellcheck = false;
  notes.textContent = r.notes || '';
  _onBlurCommit(notes, () => { const v = (notes.textContent || '').trim(); if (v !== (r.notes || '')) { r.notes = v; markDirty(); } });
  card.appendChild(notes);

  return card;
}
/* Shared Owner field builder used by both the risk and issue phases. */
function _rcOwnerField(r) {
  const ownerField = document.createElement('span');
  ownerField.className = 'rc-meta-field';
  ownerField.innerHTML = `Owner: <input type="text" list="stakeholderList" placeholder="(none)" value="${esc(r.owner || '')}">`;
  ownerField.querySelector('input').addEventListener('change', (e) => { const v = (e.target.value || '').trim(); if (v !== (r.owner || '')) { r.owner = v; markDirty(); } });
  return ownerField;
}

/* After a risk mutation that affects the rendered card (type, score, status):
   re-render the whole list + matrix + dashboard. For pure cosmetic-stable
   changes (statement/notes/owner edits), the inline edit alone is enough and
   we skip the re-render to avoid stealing focus mid-typing. */
function _afterRiskMutation() {
  renderRisks();
  _refreshDashboardFromRisks();
}

function deleteRisk(id) {
  const idx = RISKS.findIndex(r => r.id === id);
  if (idx < 0) return;
  const r = RISKS[idx];
  const noun = r.phase === 'issue' ? 'issue' : 'risk';
  const label = (r.statement && r.statement.trim()) ? '"' + _escapeHtml(r.statement.trim()) + '"' : ('this ' + noun);
  showConfirm('Delete ' + noun + '?',
    'This will remove ' + label + ' (' + _escapeHtml(_registerId(r)) + ') from the register.<br><br>You\'ll have 6 seconds to undo.',
    () => {
      const i2 = RISKS.findIndex(x => x.id === id);
      if (i2 < 0) return;
      const removed = RISKS.splice(i2, 1)[0]; const removedIndex = i2;
      markDirty(); _afterRiskMutation();
      /* v13.1 (A49): the confirm asks "Delete issue?" and both toasts then said "Risk".
         `noun` is already computed above for the confirm — use it, so the three messages
         about one deletion agree with each other. Capitalised for the sentence start. */
      const Noun = noun.charAt(0).toUpperCase() + noun.slice(1);
      showUndoToast(Noun + ' deleted', () => {
        RISKS.splice(removedIndex, 0, removed);
        markDirty(); _afterRiskMutation();
        showToast(Noun + ' restored', { type: 'info' });
      });
    });
}

